import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { format, formatDuration, parseISO, startOfDay, subDays } from "date-fns";
import { intervalToDuration } from "date-fns/esm";
import { enUS } from "date-fns/locale";
import { utcToZonedTime } from "date-fns-tz";
import {
  Datagrid,
  Filter,
  FunctionField,
  Identifier,
  Record,
  Responsive,
  SimpleList,
  TextField,
  required,
  useListContext,
  useLocale,
  useRefresh,
  useTranslate,
} from "react-admin";
import { DisconnectedDrivingLocationGroupDto } from "../../../common/data/service-proxies";
import { AppbarCompaniesFilter } from "../../common/data-grid-inputs/AppBarCompaniesFilter";
import { AppbarGroupsFilter } from "../../common/data-grid-inputs/AppBarGroupsFilter";
import { Void } from "../../common/data-grid-fields/Void";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { RaDriverPicker } from "../../common/form/RaDriverPicker";
import { RaVehiclePicker } from "../../common/form/RaVehiclePicker";
import {
  dateFnsLocaleMap,
  ProLogsLocale,
} from "../../../common/util/localization";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import disconnectedDrivings from ".";
import { useDisconnectedDrivingUpdates } from "../../../common/hooks/useDisconnectedDrivingUpdates";

type DisconnectedDrivingRecord = DisconnectedDrivingLocationGroupDto & Record;

const MAXIMUM_PERIOD_DAYS = 31;

const validateDateRange = (
  value: unknown,
  allValues: { dateFrom?: unknown; dateTo?: unknown }
) => {
  if (!value || !allValues.dateFrom || !allValues.dateTo) {
    return undefined;
  }

  const from = startOfDay(new Date(String(allValues.dateFrom)));
  const to = startOfDay(new Date(String(allValues.dateTo)));
  const inclusiveDays =
    Math.round((to.getTime() - from.getTime()) / 86400000) + 1;

  if (inclusiveDays < 1) {
    return "resources.hos-disconnected-drivings.validation.invalidRange";
  }

  if (inclusiveDays > MAXIMUM_PERIOD_DAYS) {
    return "resources.hos-disconnected-drivings.validation.maximumRange";
  }

  return undefined;
};

const ListFilter = (props: any) => (
  <Filter {...props}>
    <GenericDateOrTimePicker
      PickerComponent={DatePicker}
      options={{ autoOk: true }}
      className="list-view-filter"
      label="resources.hos-disconnected-drivings.labels.dateFrom"
      source="dateFrom"
      validate={[required(), validateDateRange]}
      alwaysOn
    />
    <GenericDateOrTimePicker
      PickerComponent={DatePicker}
      options={{ autoOk: true }}
      className="list-view-filter"
      label="resources.hos-disconnected-drivings.labels.dateTo"
      source="dateTo"
      validate={[required(), validateDateRange]}
      alwaysOn
    />
    <RaDriverPicker
      className="list-view-filter"
      label="resources.hos-disconnected-drivings.labels.driver"
      source="driverId"
      alwaysOn
    />
    <RaVehiclePicker
      className="list-view-filter"
      label="resources.hos-disconnected-drivings.labels.vehicleName"
      source="vehicleId"
      alwaysOn
    />
  </Filter>
);

export const formatDisconnectedDrivingTime = (
  value?: string,
  timeZone?: string | null
) => {
  if (!value) {
    return "—";
  }

  try {
    const date = parseISO(value);
    return timeZone
      ? format(utcToZonedTime(date, timeZone), "MM/dd/yyyy h:mm a")
      : format(date, "MM/dd/yyyy h:mm a");
  } catch (_) {
    return "—";
  }
};

const isOngoingSegment = (record?: DisconnectedDrivingRecord) =>
  record?.isOngoing ?? !record?.endTime;

const StartTime = ({ record }: { record?: DisconnectedDrivingRecord }) => (
  <span>
    {formatDisconnectedDrivingTime(
      record?.startTime,
      record?.homeTerminalTimeZone
    )}
  </span>
);

const OngoingLabel = () => {
  const translate = useTranslate();
  return (
    <Typography component="span" variant="body2" color="textPrimary">
      {translate("resources.hos-disconnected-drivings.labels.ongoing")}
    </Typography>
  );
};

const EndTime = ({ record }: { record?: DisconnectedDrivingRecord }) => {
  if (isOngoingSegment(record)) {
    return <OngoingLabel />;
  }

  return (
    <span>
      {formatDisconnectedDrivingTime(
        record?.endTime,
        record?.homeTerminalTimeZone
      )}
    </span>
  );
};

const SegmentDuration = ({
  record,
}: {
  record?: DisconnectedDrivingRecord;
}) => {
  const locale = useLocale();
  const dateFnsLocale = dateFnsLocaleMap[locale as ProLogsLocale] ?? enUS;

  if (isOngoingSegment(record)) {
    return <OngoingLabel />;
  }

  if (!record?.startTime || !record.endTime) {
    return <span>—</span>;
  }

  try {
    return (
      <span>
        {formatDuration(
          intervalToDuration({
            start: parseISO(record.startTime),
            end: parseISO(record.endTime),
          }),
          { locale: dateFnsLocale }
        )}
      </span>
    );
  } catch (_) {
    return <span>—</span>;
  }
};

const DriverEvidence = ({ record }: { record?: DisconnectedDrivingRecord }) => {
  const translate = useTranslate();
  return (
    <span>
      {record?.driverName ??
        translate("resources.hos-disconnected-drivings.labels.notAttributed")}
    </span>
  );
};

const MobileEvidence = ({ record }: { record?: Record }) => {
  const item = record as DisconnectedDrivingRecord | undefined;
  const translate = useTranslate();
  return (
    <Box display="flex" flexDirection="column" gridGap={8} mt={1}>
      <Typography variant="body2">
        {translate("resources.hos-disconnected-drivings.labels.driver")}:{" "}
        <DriverEvidence record={item} />
      </Typography>
      <Typography variant="body2">
        <StartTime record={item} />
        {" – "}
        <EndTime record={item} />
      </Typography>
      <Typography variant="body2">
        {translate("resources.hos-disconnected-drivings.labels.duration")}:{" "}
        <SegmentDuration record={item} />
      </Typography>
    </Box>
  );
};

const DisconnectedDrivingResults = (props: any) => {
  const { loading, error, loaded, total } = useListContext(props) as any;
  const refresh = useRefresh();
  useDisconnectedDrivingUpdates(refresh);
  const translate = useTranslate();

  if (loading) {
    return (
      <Box
        role="status"
        aria-live="polite"
        display="flex"
        alignItems="center"
        justifyContent="center"
        gridGap={12}
        minHeight={220}
      >
        <CircularProgress size={28} />
        <Typography>
          {translate("resources.hos-disconnected-drivings.states.loading")}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box role="alert" textAlign="center" p={4}>
        <Typography variant="h6" gutterBottom>
          {translate("resources.hos-disconnected-drivings.states.error")}
        </Typography>
        <Button color="primary" variant="contained" onClick={() => refresh()}>
          {translate("resources.hos-disconnected-drivings.states.retry")}
        </Button>
      </Box>
    );
  }

  if (loaded && total === 0) {
    return <ProLogsEmpty iconType={disconnectedDrivings.icon} />;
  }

  return (
    <Responsive
      small={
        <SimpleList
          className="simple-list-wrapper"
          primaryText={(record) =>
            `${record.companyName ?? "—"} · ${record.vehicleName ?? "—"}`
          }
          secondaryText={(record) => <MobileEvidence record={record} />}
          linkType={false}
        />
      }
      medium={
        <Datagrid>
          <FunctionField<DisconnectedDrivingRecord>
            source="driverName"
            label="resources.hos-disconnected-drivings.labels.driver"
            render={(record) => <DriverEvidence record={record} />}
          />
          <TextField
            source="vehicleName"
            label="resources.hos-disconnected-drivings.labels.vehicleName"
          />
          <FunctionField<DisconnectedDrivingRecord>
            source="startTime"
            label="resources.hos-disconnected-drivings.labels.startTime"
            render={(record) => <StartTime record={record} />}
          />
          <FunctionField<DisconnectedDrivingRecord>
            source="endTime"
            label="resources.hos-disconnected-drivings.labels.endTime"
            render={(record) => <EndTime record={record} />}
          />
          <FunctionField<DisconnectedDrivingRecord>
            source="duration"
            sortable={false}
            label="resources.hos-disconnected-drivings.labels.duration"
            render={(record) => <SegmentDuration record={record} />}
          />
        </Datagrid>
      }
    />
  );
};

export const DisconnectedDrivingsList = (props: any) => {
  const [selectedGroup, setSelectedGroup] = useState<Identifier>("");
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const handleCompaniesChange = useCallback(
    (companyIds: string[]) => setSelectedCompanies(companyIds),
    []
  );

  const filter = {
    ...(selectedCompanies.length
      ? { companyIds: selectedCompanies.join(",") }
      : {}),
    ...(selectedGroup ? { groupId: selectedGroup } : {}),
  };

  return (
    <div className="content-container">
      <AppbarCompaniesFilter
        selectedCompanies={selectedCompanies}
        onSelectedCompaniesChange={handleCompaniesChange}
      />
      <AppbarGroupsFilter
        selectedGroup={selectedGroup}
        onSelectedGroupChange={setSelectedGroup}
      />
      <DefaultProLogsList
        title="resources.hos-disconnected-drivings.name"
        actions={<Void />}
        canExport={false}
        filters={<ListFilter />}
        filter={filter}
        filterDefaultValues={{
          dateFrom: startOfDay(subDays(new Date(), 30)).toISOString(),
          dateTo: startOfDay(new Date()).toISOString(),
        }}
        bulkActionButtons={false}
        empty={<ProLogsEmpty iconType={disconnectedDrivings.icon} />}
        sort={{ field: "endTime", order: "DESC" }}
        {...props}
      >
        <DisconnectedDrivingResults />
      </DefaultProLogsList>
    </div>
  );
};
